import {
  ChevronLeft,
  PencilLine,
  CircleChevronLeft,
  CircleChevronRight,
  MapPin,
} from "lucide-react";
import { useEffect, useState, useRef } from "react";
import headerBg from "../../assets/images/doctorDetails-bg.png";
import { DoctorDetailsType } from "../../types/doctor";
import DoctorHealthPlaceholder from "../../assets/images/default_avatar.jpg";
import { DoctorService } from "../../services/api/doctor.service";
import { useAuth } from "../../context/AuthContext";
import { formatAge } from "../../shared/AddNewPatientModal";
import toast from "react-hot-toast";
import PreferencesModal from "../../shared/DoctorAppointmentSettings/PreferencesModal";
import DoctorPrescriptionTab from "./prescription/DoctorPrescriptionTab";

// Appointment interface removed

interface PatientHistory {
  id: string;
  profileImage: string;
  patientName: string;
  mobileNumber: string;
  age: string;
  gender: string;
  appointmentTime: string;
  appointmentDate: string;
}

interface DoctorDetailsPropsType {
  onClose?: () => void;
  doctor: DoctorDetailsType;
  onEdit: (doctor: DoctorDetailsType) => void;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const formatToYYYYMMDD = (d: Date) => d.toISOString().split("T")[0];

  if (formatToYYYYMMDD(date) === formatToYYYYMMDD(today)) {
    return "Today";
  }

  if (formatToYYYYMMDD(date) === formatToYYYYMMDD(tomorrow)) {
    return "Tomorrow";
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export function DoctorDetails({
  onClose,
  doctor,
  onEdit,
}: DoctorDetailsPropsType) {
  const { user } = useAuth();
  const [doctorDetails, setDoctorDetails] = useState<DoctorDetailsType | null>(
    null,
  );
  const [patientHistory, setPatientHistory] = useState<PatientHistory[]>([]);
  const [appointmentDates] = useState<string[]>([]);

  const [date, setDate] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /* New state for appointment settings */
  // const [appointmentSettings, setAppointmentSettings] = useState<any[]>([]); // Refactored: managed by PreferencesModal
  const [activeTab, setActiveTab] = useState("overview"); // overview | settings

  // const [showPreferenceModal, setShowPreferenceModal] = useState(false);
  // const [editingPreference, setEditingPreference] = useState<Preference | null>(null);
  // const [preferenceType, setPreferenceType] = useState("Online");
  // const [isMonthly, setIsMonthly] = useState(false); // Local state for toggle

  const FetchDoctorProfile = async () => {
    const doctorId = doctor.userId;
    try {
      setLoading(true);
      if (doctorId) {
        const response = await DoctorService.getDoctorAdminDetails(doctorId);
        if (response.success && response.data) {
          setDoctorDetails(response.data);

          // if (response.data.appointmentSettings) {
          //   setAppointmentSettings(response.data.appointmentSettings);
          // }
          // setIsMonthly(response.data.isMonthly || false);
        }
      } else {
        toast.error("Doctor Details not found");
      }
    } catch (error) {
      console.error("Error loading Doctor Profile:", error);
      toast.error("Failed to load Doctor Profile");
    } finally {
      setLoading(false);
    }
  };

  // Removed unused handlers: handleEditPreference, handleAddPreference, handleSavePreference, handleToggleSchedulingType

  useEffect(() => {
    FetchDoctorProfile();
  }, [doctor.userId]);

  useEffect(() => {
    const fetchPatientHistory = async () => {
      if (!date) return;
      const searchDate = date;
      const data = await DoctorService.getPatientHistoryByDate(
        doctor.userId,
        searchDate,
      );
      setPatientHistory(data.data);
      console.log("Patient History", data);
    };
    fetchPatientHistory();
    //console.log("Patient Data :");
  }, [date, doctor.userId]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 135 + 16; // button width (135px) + gap (16px)
    const newPosition =
      direction === "left"
        ? scrollPosition - scrollAmount
        : scrollPosition + scrollAmount;

    container.scrollTo({
      left: newPosition,
      behavior: "smooth",
    });
    setScrollPosition(newPosition);
  };
  const handleDateClick = (date: any) => {
    setDate(date);
    setSelectedDate(date);
  };
  return (
    <section className="w-full h-full overflow-y-auto hiddenScrollbar ">
      {/* Heading and back button box */}
      <h3 className={`flex items-center justify-start gap-4`}>
        <button
          className="w-8 h-8 flex items-center justify-center bg-gray-300/40 text-red-600 rounded-full"
          onClick={onClose}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <span className="font-semibold text-lg">Doctor Details</span>
      </h3>

      <div className="my-4 bg-white border border-gray-400/40 rounded-md w-full min-h-[975px] h-auto pb-6">
        <div
          className="w-full h-[125px] rounded-t-md bg-cover bg-no-repeat relative"
          style={{ backgroundImage: `url(${headerBg})` }}
        >
          {/* Edit profile button */}
          {user?.role?.toLocaleLowerCase() === "admin" && (
            <button
              onClick={() => onEdit(doctor)} // Trigger edit when clicked
              className="absolute top-2 right-3 bg-white flex items-center justify-center gap-2 font-semibold text-sm border border-gray-400/40 rounded-md p-2 text-[#005AAB]"
            >
              <PencilLine className="w-4 h-4" />
              Edit Profile
            </button>
          )}

          {/* <button
            type="button"
            className="flex justify-center items-center py-1.5 px-3 absolute top-20 right-3 z-30"
            style={{
              border: "1px solid #A33474",
              color: "#A33474",
              borderRadius: "8px",
            }}
            onClick={handlePrescriptionPreview}
          >
            <span className="mr-2">
              <Eye className="w-5 h-5" />
            </span>
            Prescription Template
          </button>

          <PrescriptionTemplateModal
            showPreview={showPreview}
            onClose={handleClosePreview}
            doctorDetails={doctor}
          /> */}

          {/*Profile and  Doctor details */}
          <div className="w-full absolute bottom-[-45px] left-0  px-4 flex items-center justify-start gap-3">
            {/* profile */}
            <div className="h-36 w-36 rounded-full border border-gray-400/20 overflow-hidden relative top-4">
              <img
                src={
                  doctor.profileImage instanceof File
                    ? URL.createObjectURL(doctor.profileImage)
                    : doctor.profileImage || DoctorHealthPlaceholder
                }
                alt="doctor-name"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Doctor details  */}
            <div className="w-[calc(100%-100px)] flex items-start justify-between">
              <div>
                <p className="text-lg font-semibold ">{doctor.name}</p>
                <div className="flex gap-4 justify-start text-[#383A3B] text-sm w-full">
                  <span>{doctor.specialization}</span>
                  <span>Exp: {doctor.experience} years</span>

                  {/* <span>Mobile: {doctor.mobileNumber}</span> */}

                  <span>
                    {user?.role.toLowerCase() === "medicalcamp"
                      ? `Camp Fee: ${doctor.amount || 0}.00`
                      : `OPD Rate: ${doctor.opRate}.00`}
                  </span>
                </div>
                <div className="text-[#8A8A8A] flex relative top-6 gap-1">
                  <MapPin className="w-5 h-5" />
                  <span className="text-sm">
                    {doctor.hospitalName}, {doctor.address}, {doctor.city} -{" "}
                    {doctor.pincode}, {doctor.state}
                  </span>
                </div>
              </div>
              {/* <button className="rounded-sm px-4 py-2 border border-[#A33474] text-[#A33474] text-sm font-medium flex items-center justify-center gap-2">
                <Eye className="w-[3] h-[3]" /> Prescription Template
              </button> */}
            </div>
          </div>
        </div>
        {/* DOCTOR HISTORY*/}
        <div className="flex items-stretch justify-between gap-2">
          <div className="w-[90%] ml-8">
            <div className="flex gap-4 border-b border-gray-200 mt-14 mb-4">
              <button
                className={`py-2 px-4 font-medium text-sm ${
                  activeTab === "overview"
                    ? "text-[#771D51] border-b-2 border-[#771D51]"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
              <button
                className={`py-2 px-4 font-medium text-sm ${
                  activeTab === "settings"
                    ? "text-[#771D51] border-b-2 border-[#771D51]"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("settings")}
              >
                Appointment Settings
              </button>
              <button
                className={`py-2 px-4 font-medium text-sm ${
                  activeTab === "prescription"
                    ? "text-[#771D51] border-b-2 border-[#771D51]"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("prescription")}
              >
                Prescription
              </button>
            </div>

            {activeTab === "overview" ? (
              <>
                {/* About section */}
                <p className="flex flex-col justify-start items-start gap-2 p-2">
                  <span className="beforeHeading relative  py-2 text-[#771D51] font-medium ">
                    About
                  </span>
                  {doctorDetails?.about ? doctorDetails.about : "No about"}
                </p>

                {/* Education & Qualifications */}
                <div className="flex flex-col justify-start items-start gap-2 mt-4 p-2">
                  <span className="beforeHeading relative py-2 text-[#771D51] font-medium">
                    Education & Qualifications
                  </span>
                  <div className="flex flex-row gap-2">
                    {Array.isArray(doctor.education) &&
                      doctor.education.length > 0 &&
                      doctor.education[0].split(",").map((edu, index) => (
                        <div
                          key={index}
                          className="border-t border-b px-3 py-1 rounded-2xl"
                          style={{ backgroundColor: "#ECECEC" }}
                        >
                          {edu.trim()}
                        </div>
                      ))}
                    {doctor.specialization.length > 0 && (
                      <div
                        className="border-t border-b  px-3 py-1 rounded-2xl"
                        style={{ backgroundColor: "#ECECEC" }}
                      >
                        {doctor.specialization?.join(", ") || ""}
                      </div>
                    )}
                  </div>
                </div>

                {/* patient history */}
                {loading ? (
                  <>
                    <div className="mt-3 p-2">
                      <div className="h-5 w-32 bg-gray-200 rounded mb-3" />
                      <div className="border border-gray-400/60 rounded-lg w-full h-[450px] p-2">
                        {/* Date Navigation */}
                        <div className="flex gap-4 mb-4">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <div
                              key={i}
                              className="h-8 w-32 bg-gray-200 rounded"
                            />
                          ))}
                        </div>

                        {/* Table Skeleton */}
                        <div className="space-y-4">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="flex gap-4 h-12 w-full">
                              <div className="w-[30%] bg-gray-100 rounded" />
                              <div className="w-[20%] bg-gray-100 rounded" />
                              <div className="w-[15%] bg-gray-100 rounded" />
                              <div className="w-[15%] bg-gray-100 rounded" />
                              <div className="w-[20%] bg-gray-100 rounded" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="mt-3 p-2">
                    <h4 className="text-[#383A3B] font-medium text-sm">
                      Patients History
                    </h4>

                    <div className="mt-3 border border-gray-400/60 rounded-lg w-full h-[450px] p-2">
                      {/* <div> No Patient History</div> */}
                      {/* Data strips */}
                      <div className="hiddenScrollbar flex items-center gap-4 w-full h-[8%] overflow-x-hidden whitespace-nowrap">
                        <button
                          className="p-2 rounded-full hover:bg-gray-300 transition-all"
                          onClick={() => scroll("left")}
                          disabled={scrollPosition <= 0}
                        >
                          <CircleChevronLeft className="w-5 h-5" />
                        </button>

                        <div
                          ref={scrollContainerRef}
                          className="flex flex-row w-[calc(135px*5+16px*4)] overflow-hidden"
                        >
                          <div className="flex flex-row gap-4">
                            {appointmentDates.length > 0 ? (
                              appointmentDates.map((date, index) => (
                                <button
                                  key={index}
                                  onClick={() => handleDateClick(date)}
                                  className={`shrink-0 transition-all cursor-pointer rounded-md text-sm font-medium w-[135px] flex items-center justify-center px-2 py-1 
                                ${
                                  selectedDate === date
                                    ? "text-[#771D51] bg-[#F1F1F6]/100 "
                                    : "bg-gray-100  hover:bg-gray-300"
                                }`}
                                >
                                  {formatDate(date)}
                                </button>
                              ))
                            ) : (
                              <button
                                className={`shrink-0 transition-all cursor-pointer rounded-md text-sm font-medium w-[135px] flex items-center justify-center px-2 py-1 
                                text-[#771D51] bg-[#F1F1F6]`}
                              >
                                {"Today"}
                              </button>
                            )}
                          </div>
                        </div>

                        <button
                          className="p-2 rounded-full hover:bg-gray-300 transition-all"
                          onClick={() => scroll("right")}
                          disabled={scrollPosition >= 3 * (135 + 16)}
                        >
                          <CircleChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                      {/* table  */}

                      <div className="relative h-[92%]">
                        <table className="w-full my-5 text-sm text-center table-fixed">
                          <thead
                            className="sticky top-0 z-10"
                            style={{ backgroundColor: "#F0FFFF" }}
                          >
                            <tr className="text-gray-600">
                              <th className="w-[30%] px-[30px] py-2 font-normal">
                                Name
                              </th>
                              <th className="w-[20%] px-4 py-2 font-normal">
                                Mobile Number
                              </th>
                              <th className="w-[15%] px-4 py-2 font-normal">
                                Age
                              </th>
                              <th className="w-[15%] px-4 py-2 font-normal">
                                Gender
                              </th>
                              <th className="w-[20%] px-4 py-2 font-normal">
                                Appointment Time
                              </th>
                            </tr>
                          </thead>
                        </table>
                        {appointmentDates.length > 0 ? (
                          <div className="overflow-y-auto h-[77%] hiddenScrollbar">
                            <table className="w-full text-sm text-center table-fixed">
                              <tbody>
                                {patientHistory &&
                                  patientHistory.map((value, index) => (
                                    <tr key={index}>
                                      <td className="w-[30%] px-4 py-2">
                                        <div className="flex items-center gap-2">
                                          <div className="w-[30%]">
                                            <img
                                              src={
                                                value.profileImage === ""
                                                  ? DoctorHealthPlaceholder
                                                  : value.profileImage
                                              }
                                              alt="Profile"
                                              className="w-10 h-10 rounded-full"
                                            />
                                          </div>
                                          <p className="w-[75%] text-start">
                                            {value.patientName}
                                          </p>
                                        </div>
                                      </td>

                                      <td className="w-[20%] px-4 py-2">
                                        {value.mobileNumber}
                                      </td>
                                      <td className="w-[15%] px-4 py-2">
                                        {formatAge(value.age)}
                                      </td>
                                      <td className="w-[15%] px-4 py-2">
                                        {value.gender}
                                      </td>
                                      <td className="w-[20%] px-4 py-2">
                                        {value.appointmentTime}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="mt-3 flex items-center justify-center h-[200px] rounded-lg w-full p-2">
                            <div className="text-[#8A8A8A]">
                              {" "}
                              No Patient History
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : activeTab === "settings" ? (
              // PreferencesModal replaces the old inline preferences UI
              <div className="overflow-y-auto hiddenScrollbar">
                <PreferencesModal doctorId={doctor.userId} />
              </div>
            ) : (
              // Prescription Tab
              <div className="overflow-y-auto hiddenScrollbar">
                <DoctorPrescriptionTab
                  doctor={doctor}
                  doctorId={doctor.userId}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
